import { memo, useCallback } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { MenuItem, ListItemIcon, Skeleton, Typography, Divider } from '@mui/material'
import { ContentExternalStore } from '@nativewaves/nw-cloud-sdk/content'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { SnackPanelActions, SnackPanelActionButton } from '@shared/components/Snackbar'
import { RowActionsProps } from '@shared/components/Table'
import { useExternalStoreCreateMutation, useExternalStoreDeleteMutation } from 'hooks/mutations/content'

const RowActionsRaw = ({ row, onClose }: RowActionsProps<ContentExternalStore>) => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: createExternalStore, ...externalStoreCreateMutation } = useExternalStoreCreateMutation()
  const { mutate: deleteExternalStore } = useExternalStoreDeleteMutation()

  const handleDuplicateExternalStore = useCallback(() => {
    if (row.getIsExpanded()) {
      row.toggleExpanded()
    }

    createExternalStore(
      { contentExternalStoreInsert: row.original },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }, [createExternalStore, onClose, row])

  const handleDeleteExternalStore = useCallback(() => {
    if (row.getIsExpanded()) {
      row.toggleExpanded()
    }

    onClose()

    enqueueSnackbar(`${row.original.name} ${t('domain:Integration.scheduledToBeDeleted')}`, {
      description: `${t('domain:Integration.deleteActionPermanent')}`,
      variant: 'panel',
      icon: 'caution',
      Actions: ({ buttonDefaultProps, onClose: snackbarOnClose }) => (
        <SnackPanelActions>
          <SnackPanelActionButton
            {...buttonDefaultProps}
            onClick={() => {
              snackbarOnClose(true)
            }}
          >
            {t('domain:Integration.undo')}
          </SnackPanelActionButton>
        </SnackPanelActions>
      ),
      onExitHook: () => {
        deleteExternalStore({ storeId: row.original.id })
      },
    })
  }, [row, onClose, enqueueSnackbar, t, deleteExternalStore])

  return (
    <>
      <MenuItem onClick={handleDuplicateExternalStore} disabled={externalStoreCreateMutation.isPending}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {externalStoreCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemDelete onConfirm={handleDeleteExternalStore} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
